/**
 * Configuración del Sistema — migración web de con2099 + con2085.
 *
 * Tres pestañas: "Parámetros" edita la fila principal de varinicial (datos
 * de la compañía y parámetros de préstamo; solo columnas existentes en el
 * tenant, según `campos` del backend); "NCF" edita las secuencias de la
 * tabla ncf (con selector de empresa cuando multi-empresa está activo); y
 * "Multi-empresa" administra los interruptores multiempresa/
 * facturacionconjunta y el membrete fiscal de las empresas adicionales.
 */
import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import CircularProgress from "@mui/material/CircularProgress";
import SaveIcon from "@mui/icons-material/Save";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx, SettingSection, SettingRow } from "components/financepro";
import { useApi, useSucursales } from "lib/api";

const toNum = (v) => {
  const n = parseFloat(String(v || "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});

// Dentro del hub de Configuración el marco lo pone el panel derecho: la
// sección va sin tarjeta propia, solo con separación entre bloques.
const sectionBoxHub = { mb: 3 };

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 0,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

const tituloSeccion = {
  fontWeight: 800,
  color: FP.color.textPrimary,
  fontSize: "0.95rem",
  mb: 1.5,
};

// Campos de la sección Compañía (con2099). `desc` es la ayuda que se
// muestra bajo la etiqueta en la fila de configuración (estilo Bankivo).
const CAMPOS_COMPANIA = [
  { campo: "ncomp", label: "Nombre de la compañía", desc: "Nombre legal registrado" },
  { campo: "rnc", label: "RNC", desc: "Número de registro fiscal" },
  { campo: "telefono", label: "Teléfono", desc: "Número de contacto principal" },
  { campo: "email", label: "Email", desc: "Correo de contacto" },
  { campo: "direccion", label: "Dirección", desc: "Dirección física de la empresa" },
];

// Parámetros de préstamo (varinicial), agrupados por tema para pintarlos en
// subsecciones densas: numéricos y texto en grids de 3 columnas con la ayuda
// como helperText; los S/N como switches en 2 columnas.
const CAMPOS_PRESTAMOS = [
  // Límites de financiamiento
  {
    campo: "limitedias",
    label: "Límite días",
    tipo: "num",
    grupo: "limites",
    desc: "Máximo de cuotas diarias",
  },
  {
    campo: "limitesemanas",
    label: "Límite semanas",
    tipo: "num",
    grupo: "limites",
    desc: "Máximo de cuotas semanales",
  },
  {
    campo: "limitequincenas",
    label: "Límite quincenas",
    tipo: "num",
    grupo: "limites",
    desc: "Máximo de cuotas quincenales",
  },
  {
    campo: "limitemes",
    label: "Límite meses",
    tipo: "num",
    grupo: "limites",
    desc: "Máximo de cuotas mensuales",
  },
  {
    campo: "limiteinicial",
    label: "Límite inicial",
    tipo: "num",
    grupo: "limites",
    desc: "Inicial mínima exigida al facturar",
  },
  // Interés y mora
  {
    campo: "tasaus",
    label: "Tasa US$",
    tipo: "num",
    grupo: "interes",
    desc: "Tasa de cambio del dólar",
  },
  {
    campo: "mora",
    label: "Mora %",
    tipo: "num",
    grupo: "interes",
    desc: "Porcentaje de mora por atraso",
  },
  {
    campo: "diasdegracia",
    label: "Días de gracia",
    tipo: "num",
    grupo: "interes",
    desc: "Días sin mora tras el vencimiento",
  },
  {
    campo: "interesminimo",
    label: "Interés mínimo",
    tipo: "num",
    grupo: "interes",
    desc: "Piso de interés al facturar",
  },
  // Opciones (S/N)
  {
    campo: "usaconsignado",
    label: "Usa consignado",
    tipo: "sn",
    grupo: "opciones",
    desc: "Maneja mercancía en consignación",
  },
  {
    campo: "asociarprestamogps",
    label: "Asociar préstamo a GPS",
    tipo: "sn",
    grupo: "opciones",
    desc: "Vincula cada préstamo a un GPS",
  },
  {
    campo: "usachasisenpersonal",
    label: "Usa chasis en personal",
    tipo: "sn",
    grupo: "opciones",
    desc: "Pide chasis en préstamos personales",
  },
  {
    campo: "usavendedor",
    label: "Usa vendedor",
    tipo: "sn",
    grupo: "opciones",
    desc: "Registra el vendedor en cada venta",
  },
  {
    campo: "redondeocuota",
    label: "Redondear cuotas al peso",
    tipo: "sn",
    grupo: "opciones",
    desc: "La cuota de los préstamos sube al peso siguiente; la diferencia va al interés",
  },
  {
    campo: "elegirprimeracuota",
    label: "Elegir fecha de primera cuota",
    tipo: "sn",
    grupo: "opciones",
    desc: "Al crear el préstamo se puede digitar cuándo cae la primera cuota (hasta un mes)",
  },
  {
    campo: "compararconteocuadre",
    label: "Comparar conteo de cuadre",
    tipo: "sn",
    grupo: "opciones",
    desc: "El cuadre de caja muestra las diferencias contra el conteo de caja; el conteo que no cuadra se marca con problema",
  },
  {
    campo: "tratamientocodeudor",
    label: "Tratamiento de co-deudor",
    tipo: "sn",
    grupo: "opciones",
    desc: "El fiador responde como co-deudor: tiene expediente con los préstamos que garantiza y venderle (o usarlo de fiador otra vez) avisa y exige autorización",
  },
  {
    campo: "usagarantias",
    label: "Usa garantías",
    tipo: "sn",
    grupo: "opciones",
    desc: "Cada venta al cliente final (contado o crédito) genera su garantía con el plan de mantenimientos de su marca (módulo Garantías). No aplica a ventas x mayor ni consignación",
  },
  {
    campo: "normalizamarcas",
    label: "Estandarizar marcas y modelos",
    tipo: "sn",
    grupo: "opciones",
    desc: "Al registrar chasis (uno a uno, compra por lote, importación o embarque) la marca y el modelo se guardan con la escritura estándar compartida: «super tucan» / «v-150» queda SUPER TUCAN · V150. Lo que no se reconoce se guarda como se digitó",
  },
  {
    campo: "catalogoincautadores",
    label: "Incautadores por catálogo",
    tipo: "sn",
    grupo: "opciones",
    desc: "Al registrar un incauto el incautador se elige del catálogo (Incautos y Cargos → Incautadores) y solo valen los activos. Apagado, la cédula y el nombre se escriben a mano",
  },
  {
    campo: "incautosinautorizacion",
    label: "Registrar incautos sin autorización",
    tipo: "sn",
    grupo: "opciones",
    desc: "El incauto se guarda sin esperar la autorización de un AdmG. Apagado, cada incauto la pide (los de una solicitud de incauto aprobada nunca la piden)",
  },
  {
    campo: "soloserializado",
    label: "Solo serializado",
    tipo: "sn",
    grupo: "opciones",
    desc: "Inventario únicamente con chasis",
  },
  {
    campo: "completivoinicial",
    label: "Completivos al inicial",
    tipo: "sn",
    grupo: "opciones",
    desc: "Permite vender con inicial pactado mayor al cobrado; el saldo se cobra en Pagos",
  },
  {
    campo: "inicialxmayor",
    label: "Inicial en ventas x mayor",
    tipo: "sn",
    grupo: "opciones",
    desc: "La factura x mayor a crédito admite inicial pactado: se cobra todo o parte al facturar, el resto vence en la fecha pactada y se cobra junto a las cuotas",
  },
  {
    campo: "usarpreciolista",
    label: "Usar precio de lista",
    tipo: "sn",
    grupo: "opciones",
    desc: "El catálogo de precios (Marcas y Modelos) prellena precio y mínimo en el registro de chasis, las cargas por Excel y las cotizaciones",
  },
  {
    campo: "contabilidadfiscal",
    label: "Contabilidad fiscal / no fiscal",
    tipo: "sn",
    grupo: "opciones",
    desc: "El módulo contable trabaja en dos versiones: todo se registra en la no fiscal y se pasa a la fiscal",
  },
  {
    campo: "contabilizaciondiaria",
    label: "Contabilización diaria",
    tipo: "sn",
    grupo: "opciones",
    desc: "Cobros y gastos no van directo al diario: se revisan en una pre-vista por día (Contabilidad) y desde ahí se envían",
  },
  // Facturación
  {
    campo: "tasaitbis",
    label: "Tasa ITBIS %",
    tipo: "num",
    grupo: "facturacion",
    desc: "Porcentaje de ITBIS aplicado al facturar",
  },
  {
    campo: "mostraritbis",
    label: "Mostrar ITBIS",
    tipo: "sn",
    grupo: "opciones",
    desc: "Desglosa el ITBIS en las facturas",
  },
  // Otros
  {
    campo: "sucursalmatricula",
    label: "Sucursal matrícula",
    tipo: "sucursal",
    grupo: "otros",
    desc: "Central de matrículas: recibe los pedidos de las sucursales. También fija la localidad por defecto de los productos no serializados",
  },
  {
    campo: "modulos",
    label: "Módulos",
    tipo: "texto",
    grupo: "otros",
    desc: "Módulos activos del sistema",
  },
  {
    campo: "diascartaruta",
    label: "Días carta de ruta",
    tipo: "texto",
    grupo: "otros",
    desc: "Vigencia de la carta de ruta",
  },
];

const GRUPOS_PRESTAMOS = [
  { clave: "limites", label: "Límites de financiamiento" },
  { clave: "interes", label: "Interés y mora" },
  { clave: "facturacion", label: "Facturación" },
  { clave: "opciones", label: "Opciones" },
  { clave: "otros", label: "Otros" },
];

const NCF_LABELS = {
  creditofiscal: "Crédito Fiscal",
  consumidorfinal: "Consumidor Final",
  gubernamental: "Gubernamental",
  especial: "Especial",
  notacredito: "Nota de Crédito",
};

/**
 * Selector de una sucursal del catálogo que guarda solo el CÓDIGO (el
 * backend lo valida). "0" = ninguna. Un valor guardado que ya no existe en
 * el catálogo se muestra marcado para que se corrija.
 */
function SelectorSucursal({ label, desc, sucursales, value, onChange }) {
  const codigo =
    String(value || "")
      .split("-")[0]
      .trim() || "0";
  const opciones = sucursales.map((s) => ({
    codigo: String(s.label || "")
      .split("-")[0]
      .trim(),
    label: s.label,
  }));
  const conocida = codigo === "0" || opciones.some((o) => o.codigo === codigo);
  return (
    <TextField
      select
      fullWidth
      size="small"
      label={label}
      helperText={conocida ? desc : `La sucursal ${codigo} no existe en el catálogo`}
      error={!conocida}
      value={codigo}
      onChange={(e) => onChange(e.target.value)}
      sx={fpInputSx}
    >
      <MenuItem value="0">Ninguna</MenuItem>
      {!conocida && <MenuItem value={codigo}>{codigo} (no existe)</MenuItem>}
      {opciones.map((o) => (
        <MenuItem key={o.codigo} value={o.codigo}>
          {o.label}
        </MenuItem>
      ))}
    </TextField>
  );
}

export default function ConfiguracionSistema({ enHub } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  // Selectores de sucursal (sucursalmatricula): todas las del catálogo.
  const sucursales = useSucursales({ todas: true });

  const [tab, setTab] = useState(0);

  // ---- Parámetros (varinicial) ----
  const [campos, setCampos] = useState([]);
  const [formVar, setFormVar] = useState({});
  const [cargandoVar, setCargandoVar] = useState(false);
  const [guardandoVar, setGuardandoVar] = useState(false);

  const cargarVarinicial = async () => {
    setCargandoVar(true);
    try {
      const { ok, data } = await post("/api/config_varinicial", {});
      if (ok) {
        const fila = data.varinicial || {};
        // Los flags se normalizan a 'S'/'N': según el tenant la columna puede
        // ser varchar ('S') o tinyint (1), y el switch/guardado asumen 'S'.
        const camposSN = new Set(
          CAMPOS_PRESTAMOS.filter((c) => c.tipo === "sn").map((c) => c.campo)
        );
        const f = {};
        (data.campos || []).forEach((c) => {
          const v = fila[c] === null || fila[c] === undefined ? "" : String(fila[c]);
          f[c] = camposSN.has(c)
            ? ["s", "si", "1", "true"].includes(v.trim().toLowerCase())
              ? "S"
              : "N"
            : v;
        });
        setFormVar(f);
        setCampos(data.campos || []);
      } else {
        showAlert({
          message: data.Error || "No se pudo leer la configuración",
          color: "error",
        });
      }
    } finally {
      setCargandoVar(false);
    }
  };

  const setCampoVar = (k) => (e) => setFormVar((f) => ({ ...f, [k]: e.target.value }));

  const guardarVarinicial = async () => {
    setGuardandoVar(true);
    try {
      const valores = {};
      CAMPOS_COMPANIA.forEach(({ campo }) => {
        if (campos.includes(campo)) valores[campo] = String(formVar[campo] || "").trim();
      });
      CAMPOS_PRESTAMOS.forEach(({ campo, tipo }) => {
        if (!campos.includes(campo)) return;
        if (tipo === "num") valores[campo] = toNum(formVar[campo]);
        else if (tipo === "sn") valores[campo] = formVar[campo] === "S" ? "S" : "N";
        else valores[campo] = String(formVar[campo] || "").trim();
      });
      const { ok, data } = await post("/api/config_varinicial_guardar", { valores });
      if (ok) {
        showAlert({ message: "Configuración guardada", color: "success" });
        cargarVarinicial();
      } else {
        showAlert({
          message: data.Error || "No se pudo guardar la configuración",
          color: "error",
        });
      }
    } finally {
      setGuardandoVar(false);
    }
  };

  // ---- Multi-empresa ----
  const [memFlags, setMemFlags] = useState({
    multiempresa: false,
    facturacionconjunta: false,
    activo: false,
  });
  const [formFlags, setFormFlags] = useState({ multiempresa: "N", facturacionconjunta: "N" });
  const [formEmpresas, setFormEmpresas] = useState({}); // {numero: {ncomp,rnc,direccion,telefono}}
  const [cargandoMem, setCargandoMem] = useState(false);
  const [guardandoMem, setGuardandoMem] = useState(false);

  const cargarMultiempresa = async () => {
    setCargandoMem(true);
    try {
      const { ok, data } = await post("/api/config_multiempresa", {});
      if (ok) {
        const flags = data.flags || {};
        setMemFlags(flags);
        setFormFlags({
          multiempresa: flags.multiempresa ? "S" : "N",
          facturacionconjunta: flags.facturacionconjunta ? "S" : "N",
        });
        const f = {};
        const l = {};
        const p = {};
        (data.empresas || []).forEach((e) => {
          f[e.empresa] = {
            ncomp: e.ncomp || "",
            rnc: e.rnc || "",
            direccion: e.direccion || "",
            telefono: e.telefono || "",
          };
          l[e.empresa] = e.logo || "";
          p[e.empresa] = e.footer || "";
        });
        setFormEmpresas(f);
        setLogosEmpresas(l);
        setFootersEmpresas(p);
      } else {
        showAlert({ message: data.Error || "No se pudo leer multi-empresa", color: "error" });
      }
    } finally {
      setCargandoMem(false);
    }
  };

  const guardarFlags = async () => {
    setGuardandoMem(true);
    try {
      const { ok, data } = await post("/api/config_multiempresa_guardar", {
        multiempresa: formFlags.multiempresa === "S",
        facturacionconjunta: formFlags.facturacionconjunta === "S",
      });
      if (ok) {
        showAlert({ message: "Multi-empresa guardado", color: "success" });
        cargarMultiempresa();
      } else {
        showAlert({ message: data.Error || "No se pudo guardar multi-empresa", color: "error" });
      }
    } finally {
      setGuardandoMem(false);
    }
  };

  const setCampoEmpresa = (numero, campo) => (e) =>
    setFormEmpresas((f) => ({ ...f, [numero]: { ...(f[numero] || {}), [campo]: e.target.value } }));

  const guardarEmpresa = async (numero) => {
    const f = formEmpresas[numero] || {};
    if (!String(f.ncomp || "").trim()) {
      showAlert({ message: "El nombre de la empresa es obligatorio", color: "warning" });
      return;
    }
    setGuardandoMem(true);
    try {
      const { ok, data } = await post("/api/config_empresa_guardar", { empresa: numero, ...f });
      if (ok) {
        showAlert({ message: "Empresa guardada", color: "success" });
        cargarMultiempresa();
      } else {
        showAlert({ message: data.Error || "No se pudo guardar la empresa", color: "error" });
      }
    } finally {
      setGuardandoMem(false);
    }
  };

  const eliminarEmpresa = async (numero) => {
    setGuardandoMem(true);
    try {
      const { ok, data } = await post("/api/config_empresa_eliminar", { empresa: numero });
      if (ok) {
        showAlert({ message: "Empresa eliminada", color: "success" });
        cargarMultiempresa();
      } else {
        showAlert({ message: data.Error || "No se pudo eliminar la empresa", color: "error" });
      }
    } finally {
      setGuardandoMem(false);
    }
  };

  const agregarEmpresa = () => {
    const numeros = Object.keys(formEmpresas).map(Number);
    const siguiente = Math.max(1, ...numeros) + 1;
    setFormEmpresas((f) => ({
      ...f,
      [siguiente]: { ncomp: "", rnc: "", direccion: "", telefono: "" },
    }));
  };

  // ---- Logo y pie de página por empresa (reportes, conduces y facturas) ----
  const [logosEmpresas, setLogosEmpresas] = useState({}); // {numero: dataURL}
  const [footersEmpresas, setFootersEmpresas] = useState({}); // {numero: dataURL}
  const [guardandoImagen, setGuardandoImagen] = useState(""); // "tipo:numero" en proceso | ""

  // El logo es un cuadro y el pie una franja ancha: cada tipo con su tope de
  // reducción, su endpoint y sus textos, mismo flujo de subir/cambiar/quitar.
  const TIPOS_IMAGEN = {
    logo: {
      endpoint: "/api/config_logo_guardar",
      campo: "logo",
      nombre: "logo",
      titulo: "Logo",
      maxW: 600,
      maxH: 300,
      vistaW: 170,
      vistaH: 56,
    },
    footer: {
      endpoint: "/api/config_footer_guardar",
      campo: "footer",
      nombre: "pie de página",
      titulo: "Pie de página",
      maxW: 1300,
      maxH: 260,
      vistaW: 340,
      vistaH: 48,
    },
  };
  const mapaImagen = (tipo) => (tipo === "logo" ? logosEmpresas : footersEmpresas);
  const setMapaImagen = (tipo) => (tipo === "logo" ? setLogosEmpresas : setFootersEmpresas);

  // Reduce la imagen en el navegador (canvas) antes de subirla: la BD guarda
  // un data URL pequeño y los PDF no cargan megas. PNG conserva transparencia.
  const leerImagen = (file, cfg) =>
    new Promise((resolve, reject) => {
      if (!/^image\/(png|jpeg)$/.test(file.type)) {
        reject(new Error(`El ${cfg.nombre} debe ser PNG o JPEG`));
        return;
      }
      const img = new window.Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        const escala = Math.min(1, cfg.maxW / img.width, cfg.maxH / img.height);
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(img.width * escala));
        canvas.height = Math.max(1, Math.round(img.height * escala));
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        URL.revokeObjectURL(url);
        resolve(canvas.toDataURL(file.type === "image/jpeg" ? "image/jpeg" : "image/png", 0.85));
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error("No se pudo leer la imagen"));
      };
      img.src = url;
    });

  const subirImagen = async (tipo, numero, file) => {
    if (!file) return;
    const cfg = TIPOS_IMAGEN[tipo];
    try {
      const imagen = await leerImagen(file, cfg);
      setGuardandoImagen(`${tipo}:${numero}`);
      const { ok, data } = await post(cfg.endpoint, { empresa: numero, [cfg.campo]: imagen });
      if (ok) {
        setMapaImagen(tipo)((m) => ({ ...m, [numero]: imagen }));
        showAlert({ message: `${cfg.titulo} guardado`, color: "success" });
      } else {
        showAlert({ message: data.Error || `No se pudo guardar el ${cfg.nombre}`, color: "error" });
      }
    } catch (err) {
      showAlert({ message: err.message || "No se pudo leer la imagen", color: "warning" });
    } finally {
      setGuardandoImagen("");
    }
  };

  const quitarImagen = async (tipo, numero) => {
    const cfg = TIPOS_IMAGEN[tipo];
    setGuardandoImagen(`${tipo}:${numero}`);
    try {
      const { ok, data } = await post(cfg.endpoint, { empresa: numero, [cfg.campo]: "" });
      if (ok) {
        setMapaImagen(tipo)((m) => ({ ...m, [numero]: "" }));
        showAlert({ message: `${cfg.titulo} quitado`, color: "success" });
      } else {
        showAlert({ message: data.Error || `No se pudo quitar el ${cfg.nombre}`, color: "error" });
      }
    } finally {
      setGuardandoImagen("");
    }
  };

  const controlImagen = (tipo, numero) => {
    const cfg = TIPOS_IMAGEN[tipo];
    const actual = mapaImagen(tipo)[numero];
    const ocupado = guardandoImagen === `${tipo}:${numero}`;
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        {actual ? (
          <Box
            component="img"
            src={actual}
            alt={cfg.titulo}
            sx={{
              maxHeight: cfg.vistaH,
              maxWidth: cfg.vistaW,
              border: `1px solid ${FP.color.border}`,
              borderRadius: 1,
              p: 0.5,
              bgcolor: "#fff",
            }}
          />
        ) : (
          <Typography sx={{ fontSize: "0.8rem", color: FP.color.textMuted }}>
            Sin {cfg.nombre}
          </Typography>
        )}
        <Button
          component="label"
          disabled={ocupado}
          sx={btnSolid(FP.color.navy, FP.color.navySoft)}
        >
          {ocupado ? "Guardando..." : actual ? `Cambiar ${cfg.nombre}` : `Subir ${cfg.nombre}`}
          <input
            hidden
            type="file"
            accept="image/png,image/jpeg"
            onChange={(e) => {
              subirImagen(tipo, numero, e.target.files && e.target.files[0]);
              e.target.value = "";
            }}
          />
        </Button>
        {actual && (
          <Button
            onClick={() => quitarImagen(tipo, numero)}
            disabled={ocupado}
            sx={{ ...btnBase, color: "#D32F2F", border: "1px solid #D32F2F" }}
          >
            Quitar
          </Button>
        )}
      </Box>
    );
  };

  // ---- NCF (con2085) ----
  const [ncfExiste, setNcfExiste] = useState(true);
  const [ncfTipos, setNcfTipos] = useState([]);
  const [ncfClaves, setNcfClaves] = useState([]);
  const [formNcf, setFormNcf] = useState({});
  const [ncfEmpresa, setNcfEmpresa] = useState(1);
  const [cargandoNcf, setCargandoNcf] = useState(false);
  const [guardandoNcf, setGuardandoNcf] = useState(false);

  const cargarNcf = async (empresa = 1) => {
    setCargandoNcf(true);
    try {
      const { ok, data } = await post("/api/config_ncf", { empresa });
      if (ok) {
        const fila = data.ncf || {};
        const f = {};
        Object.keys(fila).forEach((k) => {
          f[k] = fila[k] === null || fila[k] === undefined ? "" : String(fila[k]);
        });
        if (f.valido) f.valido = f.valido.slice(0, 10);
        setFormNcf(f);
        setNcfClaves(Object.keys(fila));
        setNcfTipos(data.tipos || []);
        setNcfExiste(Boolean(data.existe));
      } else {
        showAlert({ message: data.Error || "No se pudieron leer los NCF", color: "error" });
      }
    } finally {
      setCargandoNcf(false);
    }
  };

  const setCampoNcf = (k) => (e) => setFormNcf((f) => ({ ...f, [k]: e.target.value }));

  const guardarNcf = async () => {
    setGuardandoNcf(true);
    try {
      const valores = {};
      ncfTipos.forEach((tipo) => {
        if (ncfClaves.includes(`enc${tipo}`)) {
          valores[`enc${tipo}`] = String(formNcf[`enc${tipo}`] || "").trim();
        }
        if (ncfClaves.includes(tipo)) valores[tipo] = toNum(formNcf[tipo]);
        if (ncfClaves.includes(`${tipo}final`)) {
          valores[`${tipo}final`] = toNum(formNcf[`${tipo}final`]);
        }
      });
      if (formNcf.valido) valores.valido = formNcf.valido;
      const { ok, data } = await post("/api/config_ncf_guardar", { valores, empresa: ncfEmpresa });
      if (ok) {
        showAlert({ message: "NCF guardados", color: "success" });
        cargarNcf(ncfEmpresa);
      } else {
        showAlert({ message: data.Error || "No se pudieron guardar los NCF", color: "error" });
      }
    } finally {
      setGuardandoNcf(false);
    }
  };

  useEffect(() => {
    cargarVarinicial();
    cargarNcf(1);
    cargarMultiempresa();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const companiaVisibles = CAMPOS_COMPANIA.filter((c) => campos.includes(c.campo));
  const prestamosVisibles = CAMPOS_PRESTAMOS.filter((c) => campos.includes(c.campo));
  const cargando = tab === 0 ? cargandoVar : tab === 1 ? cargandoNcf : cargandoMem;
  const empresasAdicionales = Object.keys(formEmpresas)
    .map(Number)
    .filter((n) => n >= 2)
    .sort((a, b) => a - b);

  return (
    <Box
      component="main"
      sx={enHub ? undefined : { minHeight: "100vh", bgcolor: FP.color.surface }}
    >
      <PageHeader
        compact={enHub}
        title="Configuración del Sistema"
        subtitle="Datos de la compañía, parámetros de préstamo y comprobantes fiscales"
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={enHub ? sectionBoxHub : sectionBox}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{ mb: 2, "& .MuiTab-root": { textTransform: "none", fontWeight: 700 } }}
          >
            <Tab label="Parámetros" />
            <Tab label="NCF" />
            <Tab label="Multi-empresa" />
          </Tabs>

          {cargando && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
              <CircularProgress size={28} />
            </Box>
          )}

          {tab === 0 && !cargandoVar && (
            <>
              {companiaVisibles.length > 0 && (
                <SettingSection label="Compañía">
                  {companiaVisibles.map(({ campo, label, desc }, idx) => (
                    <SettingRow
                      key={campo}
                      label={label}
                      description={desc}
                      last={idx === companiaVisibles.length - 1}
                    >
                      <TextField
                        fullWidth
                        size="small"
                        value={formVar[campo] || ""}
                        onChange={setCampoVar(campo)}
                        sx={fpInputSx}
                      />
                    </SettingRow>
                  ))}
                </SettingSection>
              )}
              <SettingSection label="Logo y pie de página">
                <Box sx={{ py: 1.5 }}>
                  <Typography sx={{ fontSize: "0.78rem", color: FP.color.textSecondary, mb: 1.2 }}>
                    El logo aparece en el encabezado de los reportes PDF y los conduces impresos.
                    PNG o JPEG; se reduce automáticamente al subirlo. Las empresas adicionales
                    tienen su propio logo en la pestaña Multi-empresa.
                  </Typography>
                  {controlImagen("logo", 1)}
                  <Typography
                    sx={{ fontSize: "0.78rem", color: FP.color.textSecondary, mt: 2.5, mb: 1.2 }}
                  >
                    El pie de página se imprime al final de las facturas (conduce de venta al
                    detalle y a crédito). Ideal una franja ancha con servicios o datos de contacto.
                  </Typography>
                  {controlImagen("footer", 1)}
                </Box>
              </SettingSection>
              {GRUPOS_PRESTAMOS.map(({ clave, label: labelGrupo }) => {
                const delGrupo = prestamosVisibles.filter((c) => c.grupo === clave);
                if (!delGrupo.length) return null;
                if (clave === "opciones") {
                  // Switches en 2 columnas: se ve de un vistazo qué está activo.
                  return (
                    <SettingSection key={clave} label={labelGrupo}>
                      <Grid container spacing={0.5} sx={{ pt: 1.5 }}>
                        {delGrupo.map(({ campo, label, desc }) => (
                          <Grid item xs={12} sm={6} key={campo}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={formVar[campo] === "S"}
                                  onChange={(e) =>
                                    setFormVar((f) => ({
                                      ...f,
                                      [campo]: e.target.checked ? "S" : "N",
                                    }))
                                  }
                                />
                              }
                              label={
                                <Box sx={{ py: 0.5 }}>
                                  <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }}>
                                    {label}
                                  </Typography>
                                  <Typography
                                    sx={{ fontSize: "0.75rem", color: FP.color.textSecondary }}
                                  >
                                    {desc}
                                  </Typography>
                                </Box>
                              }
                              sx={{ alignItems: "center", ml: 0, mr: 0, width: "100%" }}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </SettingSection>
                  );
                }
                // Numéricos y texto en grid de 3 columnas, ayuda como helperText.
                return (
                  <SettingSection key={clave} label={labelGrupo}>
                    <Grid container spacing={2} sx={{ pt: 2 }}>
                      {delGrupo.map(({ campo, label, tipo, desc }) => (
                        <Grid item xs={12} sm={6} md={4} key={campo}>
                          {tipo === "sucursal" ? (
                            <SelectorSucursal
                              label={label}
                              desc={desc}
                              sucursales={sucursales}
                              value={formVar[campo]}
                              onChange={(codigo) => setFormVar((f) => ({ ...f, [campo]: codigo }))}
                            />
                          ) : (
                            <TextField
                              fullWidth
                              size="small"
                              label={label}
                              helperText={desc}
                              value={formVar[campo] || ""}
                              onChange={setCampoVar(campo)}
                              inputProps={
                                tipo === "num" ? { style: { textAlign: "right" } } : undefined
                              }
                              sx={fpInputSx}
                            />
                          )}
                        </Grid>
                      ))}
                    </Grid>
                  </SettingSection>
                );
              })}
              {companiaVisibles.length === 0 && prestamosVisibles.length === 0 && (
                <Alert severity="info">No hay campos de configuración en este tenant</Alert>
              )}
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
                <Button
                  startIcon={
                    guardandoVar ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />
                  }
                  onClick={guardarVarinicial}
                  disabled={
                    guardandoVar ||
                    (companiaVisibles.length === 0 && prestamosVisibles.length === 0)
                  }
                  sx={btnSolid(FP.color.navy, FP.color.navySoft)}
                >
                  Guardar
                </Button>
              </Box>
            </>
          )}

          {tab === 1 && !cargandoNcf && (
            <>
              {!ncfExiste && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  La tabla ncf no existe en este tenant
                </Alert>
              )}
              {ncfExiste && (
                <>
                  {memFlags.activo && (
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12} sm={5} md={4}>
                        <TextField
                          select
                          fullWidth
                          label="Empresa"
                          value={String(ncfEmpresa)}
                          onChange={(e) => {
                            const n = Number(e.target.value);
                            setNcfEmpresa(n);
                            cargarNcf(n);
                          }}
                          sx={fpInputSx}
                        >
                          {Object.keys(formEmpresas)
                            .map(Number)
                            .sort((a, b) => a - b)
                            .map((n) => (
                              <MenuItem key={n} value={String(n)}>
                                {n} — {formEmpresas[n]?.ncomp || `Empresa ${n}`}
                              </MenuItem>
                            ))}
                        </TextField>
                      </Grid>
                    </Grid>
                  )}
                  {ncfTipos.map((tipo) => {
                    const tienePrefijo = ncfClaves.includes(`enc${tipo}`);
                    const tieneSecuencial = ncfClaves.includes(tipo);
                    const tieneFinal = ncfClaves.includes(`${tipo}final`);
                    if (!tienePrefijo && !tieneSecuencial && !tieneFinal) return null;
                    return (
                      <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center" key={tipo}>
                        <Grid item xs={12} sm={3}>
                          <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
                            {NCF_LABELS[tipo] || tipo}
                          </Typography>
                        </Grid>
                        {tienePrefijo && (
                          <Grid item xs={4} sm={3}>
                            <TextField
                              fullWidth
                              label="Prefijo"
                              value={formNcf[`enc${tipo}`] || ""}
                              onChange={setCampoNcf(`enc${tipo}`)}
                              sx={fpInputSx}
                            />
                          </Grid>
                        )}
                        {tieneSecuencial && (
                          <Grid item xs={4} sm={3}>
                            <TextField
                              fullWidth
                              label="Secuencial actual"
                              value={formNcf[tipo] || ""}
                              onChange={setCampoNcf(tipo)}
                              inputProps={{ style: { textAlign: "right" } }}
                              sx={fpInputSx}
                            />
                          </Grid>
                        )}
                        {tieneFinal && (
                          <Grid item xs={4} sm={3}>
                            <TextField
                              fullWidth
                              label="Rango final"
                              value={formNcf[`${tipo}final`] || ""}
                              onChange={setCampoNcf(`${tipo}final`)}
                              inputProps={{ style: { textAlign: "right" } }}
                              sx={fpInputSx}
                            />
                          </Grid>
                        )}
                      </Grid>
                    );
                  })}
                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={12} sm={3} />
                    <Grid item xs={6} sm={3}>
                      <TextField
                        fullWidth
                        type="date"
                        label="Válido hasta"
                        InputLabelProps={{ shrink: true }}
                        value={formNcf.valido || ""}
                        onChange={setCampoNcf("valido")}
                        sx={fpInputSx}
                      />
                    </Grid>
                  </Grid>
                  <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
                    <Button
                      startIcon={
                        guardandoNcf ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />
                      }
                      onClick={guardarNcf}
                      disabled={guardandoNcf}
                      sx={btnSolid(FP.color.navy, FP.color.navySoft)}
                    >
                      Guardar
                    </Button>
                  </Box>
                </>
              )}
            </>
          )}

          {tab === 2 && !cargandoMem && (
            <>
              <Typography sx={tituloSeccion}>Modo multi-empresa</Typography>
              <Typography sx={{ fontSize: "0.82rem", color: FP.color.textMuted, mb: 2 }}>
                Con ambos interruptores activos, cada chasis se registra bajo una empresa y la
                facturación usa automáticamente el membrete y los NCF de la empresa dueña del
                chasis, sin elegirla al facturar. La empresa se escoge una sola vez, en el Registro
                de Chasis.
              </Typography>
              <Grid container spacing={2} sx={{ mb: 1 }}>
                <Grid item xs={6} sm={4} md={3}>
                  <TextField
                    select
                    fullWidth
                    label="Multi-empresa"
                    value={formFlags.multiempresa}
                    onChange={(e) => setFormFlags((f) => ({ ...f, multiempresa: e.target.value }))}
                    sx={fpInputSx}
                  >
                    <MenuItem value="S">Sí</MenuItem>
                    <MenuItem value="N">No</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <TextField
                    select
                    fullWidth
                    label="Facturación conjunta"
                    value={formFlags.facturacionconjunta}
                    onChange={(e) =>
                      setFormFlags((f) => ({ ...f, facturacionconjunta: e.target.value }))
                    }
                    sx={fpInputSx}
                  >
                    <MenuItem value="S">Sí</MenuItem>
                    <MenuItem value="N">No</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4} md={3} sx={{ display: "flex", alignItems: "center" }}>
                  <Button
                    startIcon={
                      guardandoMem ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />
                    }
                    onClick={guardarFlags}
                    disabled={guardandoMem}
                    sx={btnSolid(FP.color.navy, FP.color.navySoft)}
                  >
                    Guardar interruptores
                  </Button>
                </Grid>
              </Grid>
              {!memFlags.activo && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  El modo multi-empresa solo se activa cuando AMBOS interruptores están en Sí.
                </Alert>
              )}

              <Typography sx={{ ...tituloSeccion, mt: 2 }}>Empresas adicionales</Typography>
              <Typography sx={{ fontSize: "0.82rem", color: FP.color.textMuted, mb: 2 }}>
                La empresa 1 es la principal y su membrete se edita en la pestaña Parámetros. Aquí
                se administra el membrete fiscal de las demás; sus secuencias de NCF se editan en la
                pestaña NCF eligiendo la empresa.
              </Typography>
              {empresasAdicionales.map((numero) => (
                <Box
                  key={numero}
                  sx={{ border: `1px solid ${FP.color.border}`, borderRadius: 2, p: 2, mb: 2 }}
                >
                  <Typography sx={{ fontWeight: 800, mb: 1.5, fontSize: "0.88rem" }}>
                    Empresa {numero}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Nombre de la empresa"
                        value={formEmpresas[numero]?.ncomp || ""}
                        onChange={setCampoEmpresa(numero, "ncomp")}
                        sx={fpInputSx}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        fullWidth
                        label="RNC"
                        value={formEmpresas[numero]?.rnc || ""}
                        onChange={setCampoEmpresa(numero, "rnc")}
                        sx={fpInputSx}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        fullWidth
                        label="Teléfono"
                        value={formEmpresas[numero]?.telefono || ""}
                        onChange={setCampoEmpresa(numero, "telefono")}
                        sx={fpInputSx}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Dirección"
                        value={formEmpresas[numero]?.direccion || ""}
                        onChange={setCampoEmpresa(numero, "direccion")}
                        sx={fpInputSx}
                      />
                    </Grid>
                  </Grid>
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      sx={{ fontSize: "0.78rem", color: FP.color.textSecondary, mb: 0.8 }}
                    >
                      Logo (reportes y conduces)
                    </Typography>
                    {controlImagen("logo", numero)}
                  </Box>
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      sx={{ fontSize: "0.78rem", color: FP.color.textSecondary, mb: 0.8 }}
                    >
                      Pie de página (facturas)
                    </Typography>
                    {controlImagen("footer", numero)}
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
                    <Button
                      onClick={() => eliminarEmpresa(numero)}
                      disabled={guardandoMem}
                      sx={{ ...btnBase, color: "#D32F2F", border: "1px solid #D32F2F" }}
                    >
                      Eliminar
                    </Button>
                    <Button
                      startIcon={
                        guardandoMem ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />
                      }
                      onClick={() => guardarEmpresa(numero)}
                      disabled={guardandoMem}
                      sx={btnSolid(FP.color.navy, FP.color.navySoft)}
                    >
                      Guardar empresa
                    </Button>
                  </Box>
                </Box>
              ))}
              {empresasAdicionales.length === 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  No hay empresas adicionales registradas todavía.
                </Alert>
              )}
              <Button
                onClick={agregarEmpresa}
                disabled={guardandoMem}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Agregar empresa
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}
